import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useAddonTrial } from '#lib/addons/useAddonTrial';
import { BILLING_SETTINGS_PATH } from '#lib/constants/routes';
import { useStartAddon } from '#settingsFeature/models/billing/mutations';

import AddonBenefitList from './AddonBenefitList';

/**
 * Upsell for an add-on the user just bumped into inside the app (a teaser they clicked).
 * Built to convert: one concrete promise (`highlight`), the benefits as a short list, the price
 * on the button, one click to activate. Paying customers activate right here; everyone else is
 * told what has to happen first and sent to the settings. Driven by an add-on config.
 */
const AddonActivateModal = ({ addon, isOpen, onClose, title, highlight, benefits, onActivated }) => {
	const navigate = useNavigate();
	const { canActivate, canStartTrial } = useAddonTrial(addon.key);
	const startAddon = useStartAddon();
	const Icon = addon.icon;

	const handleActivate = async () => {
		try {
			await startAddon.mutateAsync(addon.key);
			onActivated?.();
			onClose();
		} catch {
			// Global mutation error handling surfaces the toast.
		}
	};

	const goToSettings = () => {
		onClose();
		navigate(BILLING_SETTINGS_PATH);
	};

	const canBuyHere = canActivate || canStartTrial;
	const ctaLabel = canStartTrial
		? `${addon.trialDays} Tage kostenlos testen`
		: `Jetzt freischalten – ${addon.priceShort}`;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="s">
			<Panel.Header icon={Icon} onClose={onClose}>
				{title ?? `${addon.name}-Add-on`}
			</Panel.Header>
			<Panel.Body className="space-y-4">
				{highlight && (
					<p className="text-sm font-medium text-primary-700 bg-primary-100 rounded-card px-3 py-2">
						{highlight}
					</p>
				)}

				{benefits?.length > 0 && <AddonBenefitList benefits={benefits} />}

				<p className="text-xs text-bgBase-800">
					{addon.priceLabel}, monatlich kündbar. Gilt sofort für alle bereits angereicherten Ansprechpartner.
				</p>
			</Panel.Body>
			<Panel.Footer stacked>
				{canBuyHere ? (
					<Button
						fullWidth
						variant="accent"
						shape="pill"
						onClick={handleActivate}
						loading={startAddon.isPending}
					>
						{ctaLabel}
					</Button>
				) : (
					<>
						<p className="text-xs text-bgBase-800 text-center">
							Das Add-on setzt ein aktives Abonnement voraus.
						</p>
						<Button fullWidth variant="accent" shape="pill" onClick={goToSettings}>
							Abo auswählen und freischalten
						</Button>
					</>
				)}
				<Button fullWidth variant="outline" onClick={onClose}>
					Später
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

AddonActivateModal.propTypes = {
	addon: PropTypes.shape({
		key: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		priceLabel: PropTypes.string.isRequired,
		priceShort: PropTypes.string.isRequired,
		trialDays: PropTypes.number.isRequired,
		icon: PropTypes.elementType.isRequired,
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string,
	highlight: PropTypes.string,
	benefits: PropTypes.arrayOf(PropTypes.string),
	onActivated: PropTypes.func,
};

export default AddonActivateModal;
